import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { getFirebaseServices } from "./firebase";

const pendingEmailStorageKey = "ratefollowup.pendingEmailLink";

export type AppUser = {
  id: string;
  email: string | null;
};

export type AuthSnapshot = {
  user: AppUser | null;
  session: null;
  configured: boolean;
};

type AuthListener = (snapshot: AuthSnapshot) => void;

function buildSnapshot(user: User | null): AuthSnapshot {
  return {
    user: user ? { id: user.uid, email: user.email } : null,
    session: null,
    configured: Boolean(getFirebaseServices()),
  };
}

export async function getAuthSnapshot(): Promise<AuthSnapshot> {
  const services = getFirebaseServices();
  if (!services) {
    return { user: null, session: null, configured: false };
  }

  await completePendingEmailLinkSignIn();
  return buildSnapshot(services.auth.currentUser);
}

export function onAuthStateChange(listener: AuthListener) {
  const services = getFirebaseServices();
  if (!services) {
    listener({ user: null, session: null, configured: false });
    return () => undefined;
  }

  return onAuthStateChanged(services.auth, (user) => {
    listener(buildSnapshot(user));
  });
}

export async function signInWithGoogle() {
  const services = requiredServices();
  const provider = new GoogleAuthProvider();
  await signInWithPopup(services.auth, provider);
}

export async function signInWithMagicLink(email: string) {
  const services = requiredServices();
  const normalizedEmail = email.trim();
  const url = `${window.location.origin}/`;

  await sendSignInLinkToEmail(services.auth, normalizedEmail, {
    url,
    handleCodeInApp: true,
  });

  window.localStorage.setItem(pendingEmailStorageKey, normalizedEmail);
}

export async function signOut() {
  const services = getFirebaseServices();
  if (!services) {
    return;
  }

  await firebaseSignOut(services.auth);
}

async function completePendingEmailLinkSignIn() {
  const services = getFirebaseServices();
  if (!services || !isSignInWithEmailLink(services.auth, window.location.href)) {
    return;
  }

  const email = window.localStorage.getItem(pendingEmailStorageKey);
  if (!email) {
    throw new Error("Email sign-in link found, but the original email is missing. Request a new link from this browser.");
  }

  await signInWithEmailLink(services.auth, email, window.location.href);
  window.localStorage.removeItem(pendingEmailStorageKey);
  window.history.replaceState({}, "", `${window.location.origin}/`);
}

function requiredServices() {
  const services = getFirebaseServices();
  if (!services) {
    throw new Error("Firebase environment variables are not configured.");
  }

  return services;
}
